import { colord } from "colord"
import { hash } from "object-code"
import { ALL_COLORS, type ColorScheme } from "../brand"
import { Bounds } from "./bounds"
import { Paths } from "./paths"
import type { Processor, Shape } from "./processor"
import { Mathf } from "./utils"

type RendererArgs = {
  cellSize: number
  rows: number
  cols: number
  lineOffset: number
  shapes?: Shape[]
  scheme: ColorScheme
  padding: number
  processor: Processor
  onColorChange?(scheme: ColorScheme): void
}

export class DrawingRenderer {
  args!: RendererArgs
  canvas!: HTMLCanvasElement
  ctx!: CanvasRenderingContext2D
  private raf = 0
  private needsResize = false

  public time = 0

  public renderScale = 2

  public rows: number = 0
  public cols: number = 0

  private scheme: ColorScheme = {
    bg: "black",
    bgColor: ALL_COLORS.black,
    fg: "white",
    fgColor: ALL_COLORS.white,
  }

  private shapes: Shape[] = []
  private queuedShapes: Shape[] | undefined
  private lastShapesSet = 0

  layers = new Map<number, AnimatedLayer>()

  setCanvas(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext("2d")!
  }

  setArgs(args: RendererArgs) {
    if (args.cellSize !== this.args?.cellSize) {
      for (const layer of this.layers.values()) {
        layer.state.cellSize = args.cellSize
        layer.needsPathUpdate = true
        layer.getResult()
      }
    }

    this.args = args
    this.needsResize = true
    this.lastShapesSet = 0

    if (args.shapes) {
      this.queueShapes(args.shapes)
    }
  }

  queueShapes(shapes: Shape[]) {
    this.queuedShapes = shapes
  }

  private setShapes(shapes: Shape[]) {
    this.args.shapes = shapes
    this.lastShapesSet = this.time

    // Group shapes by layerId
    const shapeGroups = new Map<number, Shape[]>()
    for (const shape of shapes) {
      if (!shapeGroups.has(shape.layerId)) {
        shapeGroups.set(shape.layerId, [shape])
      } else {
        shapeGroups.get(shape.layerId)!.push(shape)
      }
    }

    // Add/update layers
    for (const [layerId, shapes] of shapeGroups) {
      let layer: AnimatedLayer
      if (this.layers.has(layerId)) {
        layer = this.layers.get(layerId)!
      } else {
        layer = new AnimatedLayer(layerId)
        this.layers.set(layerId, layer)
      }
      layer.setTarget({
        cellSize: this.args.cellSize,
        cols: this.args.cols,
        rows: this.args.rows,
        lineOffset: this.args.lineOffset,
        shapes: shapes,
      })
    }

    // Hide unused layers
    for (const [layerId, layer] of this.layers) {
      if (!shapeGroups.has(layerId)) {
        layer.setTarget({
          cellSize: this.args.cellSize,
          cols: this.args.cols,
          rows: this.args.rows,
          lineOffset: this.args.lineOffset,
          shapes: [],
        })
      }
    }
  }

  start() {
    this.tick()
  }

  updateColors(dt = 0) {
    const target = this.args.scheme
    const current = this.scheme
    const speed = dt * 2
    let colorsChanged = false
    if (target.bgColor !== current.bgColor) {
      const bg = colord(current.bgColor).toRgb()
      const bg2 = colord(target.bgColor).toRgb()
      bg.r = Mathf.lerp(bg.r, bg2.r, speed)
      bg.g = Mathf.lerp(bg.g, bg2.g, speed)
      bg.b = Mathf.lerp(bg.b, bg2.b, speed)
      current.bgColor = colord(bg).toHex()
      colorsChanged = true
    }
    if (target.fgColor !== current.fgColor) {
      current.fgColor = target.fgColor
      colorsChanged = true
    }

    if (this.args.onColorChange) {
      this.args.onColorChange?.(current)
    }
  }

  easeLine(progress: number) {
    // Ease in-out quad
    return progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress
  }

  private tick(t = 0) {
    if (this.needsResize) {
      this.resize()
      this.needsResize = false
    }

    // Calculate time delta
    const dt = t == 0 || this.time === 0 ? 0 : Math.min((t - this.time) / 1000, 30 / 1000)
    this.time = t

    const { ctx, args, canvas } = this
    const { processor } = this.args

    // this.updateColors(dt);

    // Update the processor drawing effects if needed
    if (processor.cullDraw(dt)) {
      processor.updateDraw()
    }
    if (processor.needsUpdate) {
      processor.finalize(false)
      processor.needsUpdate = false
      this.queueShapes(processor.shapes)
    }

    // If there are shapes queued up, load them into the buffers, but throttle it a bit
    if (this.queuedShapes) {
      if (this.time - this.lastShapesSet > 100) {
        this.setShapes(this.queuedShapes)
        this.queuedShapes = undefined
      }
    }

    // Update and draw each layer
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.save()

    // Correct for stroke width
    const strokeWidth = (this.args.shapes?.[0].style.strokeWidth || 0) - this.args.lineOffset * this.args.cellSize * 2
    const scale = (canvas.width - strokeWidth * 2) / canvas.width
    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.scale(scale, scale)
    ctx.translate(-canvas.width / 2, -canvas.height / 2)

    // Apply padding and scale
    ctx.scale(this.renderScale, this.renderScale)
    ctx.translate(args.padding, args.padding)

    for (const layer of this.layers.values()) {
      // Update the layer
      layer.update(dt)

      // Draw each shape in the layer
      const shapes = layer.getResult()
      if (!layer.state.shapes.length) continue
      for (const [i, item] of shapes.entries()) {
        if (item.shape.style.stroke !== "none") {
          const path = new Path2D(Paths.stringify(item.commands))
          const length = item.length
          const progress = this.easeLine(item.progress)
          const offset = Math.abs(item.hash - this.time * 0.1) % length
          const drawLength = length * progress

          ctx.strokeStyle = item.shape.style.stroke
          ctx.lineWidth = item.shape.style.strokeWidth ?? 1
          ctx.globalAlpha = 1

          if (progress <= 0) {
            // draw nothing
            ctx.setLineDash([0, length])
          } else if (progress >= 1) {
            // full line, no dashes (prevents weird edge cases)
            ctx.setLineDash([])
          } else {
            // show first `drawLength`, hide the rest
            ctx.setLineDash([drawLength, length - drawLength])
          }

          // shift where along the path that visible segment starts
          ctx.lineDashOffset = -offset
          ctx.lineCap = "round"

          ctx.stroke(path)
        }
        // ctx.fillStyle = "red"
        // ctx.fillText(
        //   shape.layerId.toString(),
        //   shape.bounds.centerX * this.args.cellSize,
        //   shape.bounds.centerY * this.args.cellSize,
        // )
      }
    }

    ctx.restore()

    // Schedule the next frame
    this.raf = requestAnimationFrame((t) => this.tick(t))
  }

  // getSVGString(width: number) {
  //   let svgString = ""
  //   const height = this.canvas.height / this.renderScale
  //   const cellSize = width / this.args.cols
  //   svgString += `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${this.canvas.width} ${this.canvas.height}">`
  //   for (const layer of this.layers.values()) {
  //     // Draw each shape in the layer
  //     const paths = layer.getResult()
  //     if (!layer.state.shapes.length) continue
  //     for (const [i, path] of paths.entries()) {
  //       const shape = layer.state.shapes[i] ?? layer.state.shapes[0]
  //       let fill = "none"
  //       let stroke = "black"
  //       let lineWidth = 1
  //       if (shape.style.fill !== "none") {
  //         fill = shape.style.fill
  //       }
  //       if (shape.style.stroke !== "none") {
  //         stroke = shape.style.stroke
  //         lineWidth = shape.style.strokeWidth ?? 1
  //       }
  //       const pathData = shape.getPath(cellSize, this.args.lineOffset)
  //       const pathString = Paths.stringify(pathData)
  //       svgString += `<path d="${pathString}" fill="${fill}" stroke="${stroke}" stroke-width="${lineWidth}"/>`
  //     }
  //   }

  //   svgString += `</svg>`

  //   return svgString
  // }

  stop() {
    cancelAnimationFrame(this.raf)
  }

  private resize() {
    const { cols, rows, cellSize, padding } = this.args

    this.canvas.width = cols * cellSize * this.renderScale + padding * 2 * this.renderScale
    this.canvas.height = rows * cellSize * this.renderScale + padding * 2 * this.renderScale
  }
}

type AnimatedPathProps = {
  cols: number
  rows: number
  shapes: Shape[]
  lineOffset: number
  cellSize: number
}

class AnimatedLayer {
  state!: AnimatedPathProps

  cellSize = 0

  needsUpdate = true
  needsPathUpdate = true

  lastValue: Path2D[] = []

  private bounds: Bounds = new Bounds()

  shapes: {
    mode: "entering" | "leaving"
    progress: number
    alpha: number
    shape: Shape
    hash: number
    commands: Paths.Command[]
    length: number
  }[] = []

  constructor(public id: number) {
    this.resize()
  }

  private resize() {
    this.needsPathUpdate = true
    this.needsUpdate = true
    this.shapes = []
  }

  setTarget(state: AnimatedPathProps) {
    this.state = state

    if (this.cellSize !== state.cellSize) {
      this.resize()
    }

    this.cellSize = state.cellSize

    let modified = false

    const foundShapes = new Set<number>()
    for (const shape of state.shapes) {
      const commands = shape.getPath(state.cellSize, state.lineOffset)
      const shapeHash = hash(commands)
      const existingShape = this.shapes.find((s) => s.hash === shapeHash)
      const length = Paths.getLength(commands)
      if (existingShape) {
        existingShape.commands = commands
        existingShape.shape = shape
        existingShape.mode = "entering"
        existingShape.length = length
        foundShapes.add(shapeHash)
        continue
      }
      this.shapes.push({ mode: "entering", progress: 0, shape, hash: shapeHash, commands, length, alpha: 0 })
      foundShapes.add(shapeHash)
      modified = true
    }
    for (const s of this.shapes) {
      if (!foundShapes.has(s.hash) && s.mode !== "leaving") {
        s.mode = "leaving"
        modified = true
      }
    }

    this.needsUpdate = true
    this.needsPathUpdate = modified
  }

  update(dt = 0) {
    let changed = false

    for (const item of this.shapes) {
      let drawSpeed = (this.cellSize * 100) / item.length
      let alphaSpeed = 1
      if (item.mode === "entering") {
        const prevProgress = item.progress
        item.progress = Math.min(item.progress + dt * drawSpeed, 1)
        item.alpha = Math.min(item.alpha + dt * alphaSpeed, 1)
        if (item.progress !== prevProgress) changed = true
      } else if (item.mode === "leaving") {
        drawSpeed = 2
        alphaSpeed *= 2
        const prevProgress = item.progress
        item.progress = Math.max(item.progress - dt * drawSpeed, 0)
        item.alpha = Math.max(item.alpha - dt * alphaSpeed, 0)
        if (item.progress !== prevProgress) changed = true
      }
    }

    // Remove shapes that have finished leaving
    const beforeCount = this.shapes.length
    this.shapes = this.shapes.filter((s) => s.mode !== "leaving" || s.progress > 0)
    if (this.shapes.length !== beforeCount) changed = true

    this.needsUpdate = changed
    this.needsPathUpdate = changed
  }

  getResult() {
    return this.shapes
  }
}
